import type { FieldPolicy, Reference, TypePolicies } from '@apollo/client/cache';

import { relayStylePagination } from '@apollo/client/utilities';

// Tenant transition очищает cache; каждый снимок и набор фильтров хранится отдельно.
export const broadcastsRelayPaginationPolicy = relayStylePagination(['search', 'startedOnly', 'excludeId']);
export const broadcastAudienceEntriesRelayPaginationPolicy = relayStylePagination([
  'broadcastId',
  'snapshotId',
  'decision',
  'status',
  'search',
]);

export const clientsRelayPaginationPolicy = relayStylePagination([
  'searchQuery',
  'orderBy',
  'sortOrder',
  'segmentIds',
  'marketingSourceIds',
  'firstVisitLocationIds',
  'lastVisitLocationIds',
]);

/** Список сотрудников разделяется по статусу и локации; first/after — только курсорная пагинация. */
export const employeesRelayPaginationPolicy = relayStylePagination(['status', 'location_id']);
export const businessLocationsRelayPaginationPolicy = relayStylePagination(['search', 'status']);

export const paymentJournalRelayPaginationPolicy = relayStylePagination(['filter']);
export const paymentTypePolicies = { PaymentJournalEntry: { keyFields: ['key'] } } satisfies TypePolicies;

export const shiftsRelayPaginationPolicy = relayStylePagination(['locationId', 'dateFrom', 'dateTo']);

/** История лояльности разделяется по клиенту; first/after — только курсорная пагинация. */
export const loyaltyTransactionsRelayPaginationPolicy = relayStylePagination(['clientId']);

/** История брони разделяется по группе; first/after — только курсорная пагинация. */
export const bookingGroupAuditLogsRelayPaginationPolicy = relayStylePagination(['bookingGroupId']);

/**
 * PlatformUserRow и PlatformOrganizationRow не имеют собственного id и остаются
 * inline-узлами connection. Страницы не должны пересекаться: стабильный
 * backend tie-breaker по id обеспечивает корректную склейку без дедупликации.
 */
export const platformUsersRelayPaginationPolicy = relayStylePagination([
  'search',
  'organizationId',
  'orderBy',
  'sortOrder',
]);
/**
 * Лента штаба: опрос перечитывает голову, `before` дочитывает хвост. Обе страницы
 * сливаются по id в одну ленту, порядок задаёт `cursor` (строка сортируется как лента).
 * ponytail: лента копится без потолка, пока вкладка открыта; обрезать здесь, если станет тяжёлой.
 */
export const platformActivityFeedPolicy: FieldPolicy<Reference[]> = {
  keyArgs: ['kinds', 'organizationId'],
  merge(existing = [], incoming, { readField }) {
    const byId = new Map<string, { cursor: string; ref: Reference }>();
    for (const ref of [...existing, ...incoming]) {
      byId.set(readField<string>('id', ref) ?? '', { cursor: readField<string>('cursor', ref) ?? '', ref });
    }

    return [...byId.values()].sort((a, b) => (a.cursor < b.cursor ? 1 : -1)).map(({ ref }) => ref);
  },
};
export const platformOrganizationsRelayPaginationPolicy = relayStylePagination(['search']);
export const platformImpersonationSessionsRelayPaginationPolicy = relayStylePagination(['targetUserId', 'active']);
export const platformImpersonationActionsRelayPaginationPolicy = relayStylePagination(['sessionId']);
export const switchableOrganizationsRelayPaginationPolicy = relayStylePagination(['search']);

/**
 * Shop: каталог разделяется по поиску, витрина — по локации и поиску, движения — по товару;
 * first/after — только курсорная пагинация.
 */
export const productsRelayPaginationPolicy = relayStylePagination(['search']);
export const showcaseProductsRelayPaginationPolicy = relayStylePagination(['locationId', 'search']);
export const productMovementsRelayPaginationPolicy = relayStylePagination(['productId']);

/**
 * Обзор склада: область (точка или все доступные) и фильтры задают identity
 * списка. Без `locationId` в ключе остаток Охты показался бы под заголовком
 * Питера, а общий просмотр перетёр бы точечный.
 */
export const stockBalancesRelayPaginationPolicy = relayStylePagination(['locationId', 'search', 'productId']);

/** История склада разделяется по точке и необязательному товару. */
export const stockMovementsRelayPaginationPolicy = relayStylePagination(['locationId', 'productId']);

/** Шиномонтаж: наряды разделяются по этапу и поиску, хранения — по вкладке и поиску; оба — и по списку локаций. */
export const tireWorkOrdersRelayPaginationPolicy = relayStylePagination(['stage', 'shift', 'search', 'locationIds']);
export const tireStoragesRelayPaginationPolicy = relayStylePagination(['tab', 'search', 'locationIds']);

/**
 * `StockBalance` — проекция каталога на область доступа, а не сущность: один и
 * тот же product id в разных областях даёт разный остаток и разную разбивку.
 * Нормализация склеила бы их в одну запись, поэтому строка остаётся inline-узлом
 * connection.
 */
export const stockBalanceTypePolicies = {
  StockBalance: { keyFields: false },
} satisfies TypePolicies;

/**
 * AuthContext — синглтон без id: `keyFields: []` нормализует его в единственную
 * запись `AuthContext:{}`, чтобы узкие запросы (например, web_push capability)
 * домёрживали свои поля, а не затирали полный контекст основного auth-запроса.
 */
export const authContextTypePolicies = {
  AuthContext: { keyFields: [] },
} satisfies TypePolicies;

/**
 * Карточку автоматизации определяет пара вида и `id`: у встроенной карточки вида
 * `id` null, у созданной — свой. Нормализация по паре обновляет плитку списка
 * ответом мутации без refetch. Тихие часы — синглтон организации (`keyFields: []`,
 * как `AuthContext`): ответ мутации обновляет запрос.
 */
export const clientAutomationTypePolicies = {
  ClientAutomation: { keyFields: ['kind', 'id'] },
  ClientQuietHours: { keyFields: [] },
} satisfies TypePolicies;

/** Параметры цены шиномонтажа — синглтон организации, как тихие часы: ответ мутации обновляет запрос. */
export const tireServiceTypePolicies = {
  TireServiceSettings: { keyFields: [] },
} satisfies TypePolicies;

/**
 * Основной playlist query и необязательный loudness query возвращают частичные
 * представления одного трека. Общий ключ гарантирует field-level merge без
 * замены playlist/Howl и без сброса текущей позиции воспроизведения.
 */
export const radioTypePolicies = {
  RadioTrack: { keyFields: ['id'] },
} satisfies TypePolicies;

/**
 * Финансовые сущности Orders нормализуются по дефолтному Apollo `id` — явные
 * `keyFields: ['id']` избыточны. Value-объекты, наоборот, требуют `keyFields: false`:
 * `OrderCustomerSnapshot.id` совпадает с client id, поэтому нормализация склеила бы
 * исторические снимки разных заказов и нарушила финансовую неизменяемость;
 * `OrderCheckoutContext` — производный контекст, который не должен нормализоваться по id.
 */
export const orderTypePolicies = {
  OrderCustomerSnapshot: { keyFields: false },
  OrderCheckoutContext: { keyFields: false },
} satisfies TypePolicies;
